import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PRIVACY_POLICY_HTML from "./privacyPolicyContent";

const ACCENT = "#2563EB";
const BG = "#06080F";
const SURFACE = "#0A0D13";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#9CA3AF";
const TEXT_DIM = "#6B7280";

/**
 * /privacy renders the full Termly-exported policy HTML inside a light
 * content card. /terms is a short placeholder pointing at the founder
 * mailbox until the full ToS lands.
 */
export default function LegalPage({ title, testId, variant }) {
  const navigate = useNavigate();
  const isPrivacy = variant === "privacy";
  const isTerms = variant === "terms";

  // Inject Termly's embed script once when the Terms page mounts so the
  // <div name="termly-embed" data-id="..."> below gets rendered into a
  // full Terms of Service document.
  useEffect(() => {
    if (!isTerms) return;
    const id = "termly-jssdk";
    if (document.getElementById(id)) {
      // Script already present — re-run Termly's loader if available.
      if (window.Termly && typeof window.Termly.load === "function") {
        try { window.Termly.load(); } catch (e) { /* noop */ }
      }
      return;
    }
    const tjs = document.getElementsByTagName("script")[0];
    const js = document.createElement("script");
    js.id = id;
    js.src = "https://app.termly.io/embed-policy.min.js";
    tjs.parentNode.insertBefore(js, tjs);
  }, [isTerms]);

  return (
    <div data-testid={testId} style={{ background: BG, color: TEXT_PRIMARY, minHeight: "100vh", fontFamily: "'DM Sans', 'Inter', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

        /* Termly-generated policy content classes — needed so colours/sizes
           come through correctly inside the light card. */
        .legal-content [data-custom-class='body'],
        .legal-content [data-custom-class='body'] * { background: transparent !important; }
        .legal-content [data-custom-class='title'],
        .legal-content [data-custom-class='title'] * { font-family: Arial !important; font-size: 26px !important; color: #000000 !important; }
        .legal-content [data-custom-class='subtitle'],
        .legal-content [data-custom-class='subtitle'] * { font-family: Arial !important; color: #595959 !important; font-size: 14px !important; }
        .legal-content [data-custom-class='heading_1'],
        .legal-content [data-custom-class='heading_1'] * { font-family: Arial !important; font-size: 19px !important; color: #000000 !important; }
        .legal-content [data-custom-class='heading_2'],
        .legal-content [data-custom-class='heading_2'] * { font-family: Arial !important; font-size: 17px !important; color: #000000 !important; }
        .legal-content [data-custom-class='body_text'],
        .legal-content [data-custom-class='body_text'] * { color: #595959 !important; font-size: 14px !important; font-family: Arial !important; }
        .legal-content [data-custom-class='link'],
        .legal-content [data-custom-class='link'] * { color: #3030F1 !important; font-size: 14px !important; font-family: Arial !important; word-break: break-word !important; }

        .legal-content { color: #111827; line-height: 1.6; }
        .legal-content h1 { font-size: 28px; margin: 0 0 16px; }
        .legal-content h2 { font-size: 20px; margin: 28px 0 12px; }
        .legal-content h3 { font-size: 17px; margin: 22px 0 10px; }
        .legal-content a { color: #2563EB; }
        .legal-content ul, .legal-content ol { padding-left: 22px; }
        .legal-content table { border-collapse: collapse; width: 100%; }
        .legal-content td, .legal-content th { border: 1px solid #E5E7EB; padding: 6px 10px; }
        .legal-content bdt { display: inline; }
      `}</style>

      {/* Top nav — matches landing page style */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        padding: "0 40px", height: 56,
        display: "flex", alignItems: "center",
        background: "rgba(6,8,15,0.92)", backdropFilter: "blur(12px)",
        borderBottom: `1px solid ${BORDER}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4, flex: 1, cursor: "pointer" }} onClick={() => navigate("/")}>
          <span style={{
            fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: 17,
            color: TEXT_PRIMARY, letterSpacing: "-0.03em",
          }}>INFERENCE</span>
          <span style={{ width: 5, height: 5, borderRadius: "50%", background: ACCENT, marginLeft: 1, marginBottom: 6 }} />
        </div>
        <button
          data-testid="legal-back-to-home"
          onClick={() => navigate("/")}
          style={{
            background: "transparent", color: TEXT_MUTED, border: `1px solid ${BORDER}`,
            padding: "7px 18px", borderRadius: 7, fontSize: 13, fontWeight: 500,
            cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
          }}
        >← Back to home</button>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "120px 24px 96px" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <span style={{
            fontSize: 11, fontFamily: "'DM Mono', monospace", fontWeight: 500,
            letterSpacing: "0.12em", color: ACCENT, textTransform: "uppercase",
            display: "inline-block", padding: "5px 12px",
            background: `${ACCENT}14`, border: `1px solid ${ACCENT}33`,
            borderRadius: 5, marginBottom: 20,
          }}>Legal</span>
          <h1 style={{ fontSize: 40, fontWeight: 700, letterSpacing: "-0.03em", color: TEXT_PRIMARY, lineHeight: 1.1, margin: 0 }}>
            {title}
          </h1>
        </div>

        {isPrivacy ? (
          <div
            data-testid="privacy-policy-content"
            className="legal-content"
            style={{
              background: "#FFFFFF", color: "#111827",
              borderRadius: 12, padding: "48px 56px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
            }}
            dangerouslySetInnerHTML={{ __html: PRIVACY_POLICY_HTML }}
          />
        ) : (
          <div
            data-testid="terms-content"
            className="legal-content"
            style={{
              background: "#FFFFFF", color: "#111827",
              borderRadius: 12, padding: "48px 56px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
              minHeight: 400,
            }}
          >
            {/* Termly embed — script is injected via useEffect above */}
            <div name="termly-embed" data-id="3c1e570c-8fe9-483d-979b-8dc1123ac8a2"></div>
          </div>
        )}
      </div>
    </div>
  );
}
